import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  Button, 
  DatePicker, 
  InputNumber, 
  Space, 
  Table,
  Typography,
  Divider,
  notification,
  Spin,
  Avatar
} from 'antd';
import { PlusOutlined, DeleteOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { clientsApi, ordersApi, productsApi } from '../../utils/api'; // Importation des API
import ProductSelectionModal from './ProductSelectionModal';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const NewOrderModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [isProductModalVisible, setProductModalVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedItems([]);
      setCustomers([]);
    }
  }, [visible, form]);

  const searchCustomers = async (value) => {
    if (value.length < 3) return;
    
    setSearchingCustomer(true);
    try {
      // Appel API pour rechercher des clients par nom ou téléphone
      const response = await clientsApi.getAll({ search: value });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Erreur lors de la recherche de clients:', error);
    } finally {
      setSearchingCustomer(false);
    }
  };

  const openProductSelectionModal = () => {
    setProductModalVisible(true);
  };

  const handleProductSelect = (selectedItemData) => {
    const productId = selectedItemData.productId;
    const variantId = selectedItemData.type === 'variant' ? selectedItemData.id : null;

    const existingItemIndex = selectedItems.findIndex(
      item => item.product_id === productId && item.variant_id === variantId
    );

    let updatedItems;

    if (existingItemIndex > -1) {
      updatedItems = selectedItems.map((item, index) => {
        if (index === existingItemIndex) {
          const newQuantity = item.quantity + 1;
          return {
            ...item,
            quantity: newQuantity,
            total: item.price * newQuantity
          };
        }
        return item;
      });
      setSelectedItems(updatedItems);

    } else {
      const newItem = {
        key: Date.now(),
        product_id: productId,
        variant_id: variantId,
        item_name: selectedItemData.name,
        price: selectedItemData.price,
        sku: selectedItemData.sku,
        quantity: 1,
        total: selectedItemData.price * 1
      };
      updatedItems = [...selectedItems, newItem];
      setSelectedItems(updatedItems);
    }

    setProductModalVisible(false);
    calculateTotal(updatedItems);
  };

  const removeItem = (key) => {
    const newItems = selectedItems.filter(item => item.key !== key);
    setSelectedItems(newItems);
    calculateTotal(newItems);
  };

  const handleQuantityChange = (value, key) => {
    const updatedItems = selectedItems.map(item => {
      if (item.key === key) {
        const newItem = {
          ...item,
          quantity: value,
          total: item.price * value
        };
        return newItem;
      }
      return item;
    });

    setSelectedItems(updatedItems);
    calculateTotal(updatedItems);
  };

  const calculateTotal = (itemsToCalculate = selectedItems) => {
    const subtotal = itemsToCalculate.reduce((sum, item) => sum + (item.total || 0), 0);
    
    const shippingFee = form.getFieldValue('shipping_fee') || 0;
    const discountAmount = form.getFieldValue('discount_amount') || 0;
    
    const totalAmount = subtotal + shippingFee - discountAmount;
    
    form.setFieldsValue({ total_amount: totalAmount.toFixed(2) });
  };

  const handleCustomerSelect = (value) => {
    const customer = customers.find(c => c.id === value);
    if (customer) {
      let deliveryAddress = '';
      console.log(customer);
      if (customer.customer_addresses && customer.customer_addresses.length > 0) {
        const defaultAddr = customer.customer_addresses.find(addr => addr.is_default === true);
        const addressToUse = defaultAddr || customer.customer_addresses[0];
        
        deliveryAddress = `${addressToUse.address_line1 || ''}${addressToUse.city ? ', ' + addressToUse.city : ''}`;
      }else if(customer.default_address){
        deliveryAddress = customer.default_address;
      }

      form.setFieldsValue({
        customer_name: customer.name,
        customer_phone: customer.phone,
        delivery_address: deliveryAddress 
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (selectedItems.length === 0) {
        notification.error({
          message: 'Erreur',
          description: 'Veuillez ajouter au moins un produit à la commande.',
        });
        return;
      }
      
      setLoading(true);
      console.log(values);
      console.log(selectedItems);
      const orderData = {
        ...values,
        order_items: selectedItems.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          item_name: item.item_name,
          price: item.price,
          total: item.total
        })),
        status: 'pending',
        payment_status: 'pending',
      };
      console.log(orderData);
  
      
      const response = await ordersApi.create(orderData);
      
      if (response && response.data) {
        notification.success({
          message: 'Succès',
          description: `Commande ${response.data.id || 'N/A'} créée avec succès.`,
        });
        onSuccess();
      } else {
        throw new Error('Erreur lors de la création de la commande');
      }
    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error);
      notification.error({
        message: 'Erreur',
        description: 'Impossible de créer la commande. Veuillez réessayer.'
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Produit / Variant',
      dataIndex: 'item_name',
      key: 'item_name',
      render: (text, record) => (
        <div>
          {text || 'Produit non sélectionné'}
          {record.sku && <div style={{ fontSize: '0.8em', color: 'gray' }}>SKU: {record.sku}</div>}
        </div>
      ),
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (_, record) => (
        <InputNumber
          min={1}
          style={{ width: '100%' }}
          value={record.quantity}
          onChange={(value) => handleQuantityChange(value, record.key)}
        />
      ),
    },
    {
      title: 'Prix unitaire',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (text) => text ? `${text.toFixed(2)} XOF` : '0.00 XOF',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (text) => text ? `${text.toFixed(2)} XOF` : '0.00 XOF',
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record.key)}
        />
      ),
    },
  ];

  return (
    <Modal
      title="Nouvelle commande"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Créer la commande
        </Button>,
      ]}
    >
      <Spin spinning={loading} tip="Création de la commande...">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            delivery_date: null,
            payment_method: 'cash',
            status: 'pending',
            payment_status: 'pending',
            total_amount: 0,
            shipping_fee: 0,
            discount_amount: 0 
          }}
        >
          <Title level={5}>Informations client</Title>
          <div className="customer-search">
            <Form.Item
              name="customer_id"
              label="Rechercher un client"
            >
              <Select
                showSearch
                placeholder="Rechercher par nom ou téléphone"
                optionFilterProp="children"
                loading={searchingCustomer}
                onChange={handleCustomerSelect}
                onSearch={searchCustomers}
                filterOption={false}
                allowClear
                optionLabelProp="label"
              >
                {customers.map(customer => (
                  <Option 
                    key={customer.id} 
                    value={customer.id}
                    label={`${customer.name} (${customer.phone})`}
                  >
                    <div>
                      <UserOutlined /> {customer.name}
                    </div>
                    <div>
                      <PhoneOutlined /> {customer.phone}
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="customer-info" style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="customer_name"
              label="Nom du client"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Veuillez saisir le nom du client' }]}
            >
              <Input placeholder="Nom du client" />
            </Form.Item>

            <Form.Item
              name="customer_phone"
              label="Téléphone"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Veuillez saisir le téléphone du client' }]}
            >
              <Input placeholder="Téléphone du client" />
            </Form.Item>
          </div>

          <Form.Item
            name="delivery_address"
            label="Adresse de livraison"
            rules={[{ required: true, message: 'Veuillez saisir l\'adresse de livraison' }]}
          >
            <TextArea placeholder="Adresse de livraison" rows={2} />
          </Form.Item>

          <Divider />

          <Title level={5}>Détails de la commande</Title>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="delivery_date"
              label="Date de livraison"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Veuillez sélectionner une date de livraison' }]}
            >
              <DatePicker style={{ width: '100%' }} placeholder="Date de livraison" />
            </Form.Item>

            <Form.Item
              name="payment_method"
              label="Méthode de paiement"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Veuillez sélectionner une méthode de paiement' }]}
            >
              <Select placeholder="Sélectionner une méthode de paiement">
                <Option value="cash">Espèces</Option>
                <Option value="card">Carte bancaire</Option>
                <Option value="transfer">Virement bancaire</Option>
                <Option value="mobile_money">Mobile Money</Option>
              </Select>
            </Form.Item>
          </div>

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={5} style={{ margin: 0 }}>Produits</Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={openProductSelectionModal}>
              Ajouter un produit
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={selectedItems}
            pagination={false}
            size="small"
            locale={{ emptyText: "Aucun produit ajouté" }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '16px' }}>
            <Form.Item
              name="shipping_fee"
              label="Livraison"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: '120px' }}
                min={0}
                formatter={(value) => `${value} XOF`}
                parser={(value) => value.replace(' XOF', '')}
                onChange={() => calculateTotal()} 
              />
            </Form.Item>
            <Form.Item
              name="discount_amount"
              label="Réduction"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: '120px' }}
                min={0}
                formatter={(value) => `${value} XOF`}
                parser={(value) => value.replace(' XOF', '')}
                onChange={() => calculateTotal()}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Form.Item
              name="total_amount"
              label="Total"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: '150px' }}
                readOnly
                formatter={(value) => `${value} XOF`}
                parser={(value) => value.replace(' XOF', '')}
              />
            </Form.Item>
          </div>

          <Divider />

          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea placeholder="Notes additionnelles sur la commande" rows={3} />
          </Form.Item>
        </Form>
      </Spin>

      <ProductSelectionModal
        visible={isProductModalVisible}
        onCancel={() => setProductModalVisible(false)}
        onSelect={handleProductSelect} 
      />

    </Modal>
  );
};

export default NewOrderModal; 